import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faClose, faUserGroup } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled, { CSSProperties } from 'styled-components';

import {
  So5LeaderboardSeasonality,
  So5LeaderboardType,
} from '@sorare/core/src/__generated__/globalTypes';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelL, LabelM, LabelS } from '@sorare/core/src/atoms/typography';
import { Dialog } from '@sorare/core/src/components/dialog';
import { fantasy } from '@sorare/core/src/lib/glossary';

import { LazyRewards } from 'components/leaderboard/LazyRewards';
import { CashPrize } from 'components/rewards/CashPrize';

import type { PrizePoolDialog_so5Leaderboard } from './__generated__/index.graphql';

const Header = styled(Vertical).attrs({ gap: 1 })`
  width: 100%;
  display: grid;
  grid-template-columns: 40px 1fr 40px;
  grid-template-areas: 'left center right';
  place-items: center;
  padding: var(--double-unit);
`;
const InSeasonLabelWrapper = styled(Vertical).attrs({
  gap: 0.5,
  center: true,
})`
  grid-area: center;
`;
const IconWrapper = styled.div`
  grid-area: right;
`;
const InSeasonLabel = styled(LabelM)`
  background: linear-gradient(
    84deg,
    #f8d3da 0%,
    #b3a9f4 28.32%,
    #fbe9fb 54.01%,
    #4f94fd 100%
  );
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Cover = styled(Vertical).attrs({ gap: 8, center: true })`
  background: linear-gradient(360deg, rgba(0, 0, 0, 0) 18.52%, #000000 100%),
    var(--backgroundUrl);
`;

const Content = styled(Vertical).attrs({ gap: 2 })`
  padding: var(--double-unit);
`;

export const PrizePoolDialog = ({
  so5Leaderboard,
  onClose,
}: {
  so5Leaderboard: PrizePoolDialog_so5Leaderboard;
  onClose?: () => void;
}) => {
  return (
    <Dialog hideHeader maxWidth="xs" open onClose={onClose} fullWidth>
      <Cover
        style={
          {
            '--backgroundUrl': `url(${so5Leaderboard.so5LeaderboardGroup.seasonalHeaderBackgroundUrl})`,
          } as CSSProperties
        }
      >
        <Header>
          <InSeasonLabelWrapper>
            <LabelL bold>{so5Leaderboard.so5League.displayName}</LabelL>
            <InSeasonLabel>{so5Leaderboard.seasonalityName}</InSeasonLabel>
          </InSeasonLabelWrapper>
          <IconWrapper>
            <IconButton
              size="medium"
              color="quaternary"
              onClick={onClose}
              icon={faClose}
            />
          </IconWrapper>
        </Header>
        <img src={so5Leaderboard.so5League.headerUrl} alt="" />
      </Cover>
      <Content>
        <Vertical>
          <LabelL bold>
            <FormattedMessage {...fantasy.prizePool} />
          </LabelL>
          {!!so5Leaderboard && (
            <Horizontal>
              <FontAwesomeIcon
                icon={faUserGroup}
                size="xs"
                color="var(--c-nd-800)"
              />
              <LabelS as="p" color="var(--c-nd-800)">
                <FormattedNumber value={so5Leaderboard.so5LineupsCount} />
              </LabelS>
            </Horizontal>
          )}
        </Vertical>
        {(so5Leaderboard.seasonality === So5LeaderboardSeasonality.IN_SEASON ||
          so5Leaderboard.so5TournamentType.so5LeaderboardType ===
            So5LeaderboardType.SPECIAL_WEEKLY) &&
          !!so5Leaderboard.totalRewards.prizePool &&
          !!so5Leaderboard.mainRarityType && (
            <CashPrize
              amounts={{ cashPrize: so5Leaderboard.totalRewards.prizePool }}
              rarity={so5Leaderboard.mainRarityType}
            />
          )}
        <LazyRewards so5LeaderboardSlug={so5Leaderboard.slug} />
      </Content>
    </Dialog>
  );
};

PrizePoolDialog.fragments = {
  so5Leaderboard: gql`
    fragment PrizePoolDialog_so5Leaderboard on So5Leaderboard {
      slug
      so5LineupsCount
      seasonality
      seasonalityName
      so5TournamentType {
        id
        so5LeaderboardType
      }
      so5League {
        slug
        headerUrl
        headerBackgroundUrl
        displayName
      }
      totalRewards {
        prizePool
      }
      so5LeaderboardGroup {
        slug
        seasonalHeaderBackgroundUrl: headerBackgroundUrl(seasonality: IN_SEASON)
      }
      mainRarityType
    }
  ` as TypedDocumentNode<PrizePoolDialog_so5Leaderboard>,
};
